import React, { useEffect, useState, useCallback } from "react";
import { Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

const SUPPORTED =
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

export default function AdminPushToggle() {
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!SUPPORTED) return;
    let cancelled = false;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.register("/sw.js");
        const sub = await reg.pushManager.getSubscription();
        if (!cancelled) setSubscribed(!!sub);
      } catch (e) {
        console.warn("SW register failed", e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const subscribe = useCallback(async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Notification permission denied.");
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid-key");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });
      await api.post("/push/subscribe", {
        subscription: sub.toJSON(),
        state: null,
        user_agent: navigator.userAgent,
        is_admin: true,
      });
      setSubscribed(true);
      toast.success("Admin pings on. You'll get notified when new pending cases arrive.");
    } catch (e) {
      console.error(e);
      toast.error("Couldn't enable admin pings.");
    } finally {
      setBusy(false);
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Admin pings off.");
    } catch (e) {
      console.error(e);
      toast.error("Couldn't disable admin pings.");
    } finally {
      setBusy(false);
    }
  }, []);

  if (!SUPPORTED) {
    return (
      <span className="text-[11px] uppercase tracking-widest text-zinc-400 font-mono">
        Admin pings: browser unsupported
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={subscribed ? unsubscribe : subscribe}
      disabled={busy}
      className={`inline-flex items-center gap-1.5 border px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest transition-colors ${
        subscribed
          ? "border-green-700 bg-green-50 text-green-800 hover:bg-green-100"
          : "border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white"
      }`}
      data-testid="admin-push-toggle"
      title={
        subscribed
          ? "You'll get pinged when new RSS cases land in pending review."
          : "Enable browser pings when new RSS cases arrive in pending review."
      }
    >
      {subscribed ? <Bell size={12} /> : <BellOff size={12} />}
      {busy ? "…" : subscribed ? "Admin pings on" : "Enable admin pings"}
    </button>
  );
}
